// Robuster Axios-Client mit automatischen Retries
import axios from 'axios';
import { getApiUrl, RETRY_CONFIG, isRetryableError, calculateRetryDelay, sleep } from './api';

// Erstelle Axios-Instanz mit Basis-Konfiguration
// WICHTIG: Keine baseURL hier setzen, wird dynamisch pro Request ermittelt
const axiosClient = axios.create({
  timeout: 30000, // 30 Sekunden Timeout
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request Interceptor - setzt dynamisch die korrekte Base-URL
axiosClient.interceptors.request.use(
  (config) => {
    // Dynamisch die korrekte API-URL ermitteln (für Production-Kompatibilität)
    const apiUrl = getApiUrl();
    
    // Wenn URL relativ ist, Base-URL voranstellen
    if (config.url && !config.url.startsWith('http')) {
      config.url = `${apiUrl}${config.url.startsWith('/') ? '' : '/'}${config.url}`;
    }
    
    // Initialisiere Retry-Counter wenn nicht vorhanden
    if (config._retryCount === undefined) {
      config._retryCount = 0;
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor - für automatische Retries
axiosClient.interceptors.response.use(
  // Erfolgreiche Responses durchlassen
  (response) => response,
  
  // Fehler-Handler mit Retry-Logik
  async (error) => {
    const config = error.config;
    
    // Wenn keine Config vorhanden, kann nicht wiederholt werden
    if (!config) {
      return Promise.reject(error);
    }
    
    // Prüfe ob Retry möglich ist
    const shouldRetry = isRetryableError(error) && config._retryCount < RETRY_CONFIG.maxRetries;
    
    if (shouldRetry) {
      config._retryCount += 1;
      
      // Berechne Delay mit exponential backoff
      const delay = calculateRetryDelay(config._retryCount);
      
      // Stille Retries - kein Console-Spam
      // console.log(`🔄 Retry ${config._retryCount}/${RETRY_CONFIG.maxRetries} nach ${Math.round(delay)}ms...`);
      
      // Warte vor dem Retry
      await sleep(delay);
      
      // Wiederhole den Request
      return axiosClient(config);
    }
    
    // Keine Retries mehr möglich - Fehler weitergeben
    // Aber ohne laute Console-Errors bei bekannten temporären Problemen
    if (error.response && [500, 502, 520].includes(error.response.status)) {
      // Stille Fehler für temporäre Server-Probleme
      console.warn(`Server temporär nicht erreichbar (${error.response.status})`);
    }
    
    return Promise.reject(error);
  }
);

export default axiosClient;

// Export einer Funktion für einmalige Requests mit Retry
export const fetchWithRetry = async (url, options = {}) => {
  const { maxRetries = RETRY_CONFIG.maxRetries, ...fetchOptions } = options;
  
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await fetch(url, fetchOptions);
      
      // Prüfe auf Server-Fehler
      if (!response.ok && [500, 502, 503, 520, 521, 522, 524].includes(response.status)) {
        if (attempt < maxRetries) {
          const delay = calculateRetryDelay(attempt);
          await sleep(delay);
          continue;
        }
      }
      
      return response;
    } catch (error) {
      lastError = error;
      
      if (attempt < maxRetries) {
        const delay = calculateRetryDelay(attempt);
        await sleep(delay);
        continue;
      }
    }
  }
  
  throw lastError;
};
